import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { firestore } from "../../../../database/firebase";
import DB_PATH from "../../../../database/DB_PATH";

type Props = {
  communityId?: string;
};

type HealthReport = {
  id: string;
  planId: string;
  planTitle: string;
  updatedAt?: string;
  funnel?: {
    enrollments: number;
    planStarts: number;
    planCompletionRate: number | null;
    steps: Array<Record<string, any>>;
  };
};

type QueueItem = Record<string, any> & { id: string };

const Shell = styled.div`
  display: grid;
  gap: var(--space-5);
`;
const Card = styled.section`
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  padding: var(--space-5);
`;
const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: var(--space-4);
  margin-bottom: var(--space-4);
`;
const Title = styled.h3`
  margin: 0;
  color: var(--color-text);
  font: var(--font-weight-semibold) var(--font-size-lg) / 1.3 var(--font-sans);
`;
const Muted = styled.p`
  margin: var(--space-1) 0 0;
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
`;
const Select = styled.select`
  min-width: 240px;
  padding: var(--space-2) var(--space-3);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface);
  color: var(--color-text);
`;
const Metrics = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--space-3);
  margin-bottom: var(--space-4);
`;
const Metric = styled.div`
  padding: var(--space-3);
  border-radius: var(--radius-md);
  background: var(--color-surface-subtle);
`;
const MetricLabel = styled.div`
  color: var(--color-text-secondary);
  font-size: var(--font-size-xs);
`;
const MetricValue = styled.div`
  margin-top: var(--space-1);
  color: var(--color-text);
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-semibold);
`;
const TableWrap = styled.div`
  overflow-x: auto;
`;
const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: var(--font-size-sm);
  th,
  td {
    padding: var(--space-3);
    border-bottom: 1px solid var(--color-border-subtle);
    text-align: left;
    white-space: nowrap;
  }
  th {
    color: var(--color-text-secondary);
    font-size: var(--font-size-xs);
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
`;
const Badge = styled.span<{ $tone?: "good" | "warn" | "bad" | "neutral" }>`
  display: inline-flex;
  padding: 3px 8px;
  border-radius: 999px;
  background: ${(p) =>
    p.$tone === "good"
      ? "rgba(34, 197, 94, .12)"
      : p.$tone === "bad"
      ? "rgba(239, 68, 68, .12)"
      : p.$tone === "warn"
      ? "rgba(245, 158, 11, .14)"
      : "var(--color-surface-subtle)"};
  color: var(--color-text);
  font-size: var(--font-size-xs);
`;
const QueueGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(320px, 0.8fr);
  gap: var(--space-4);
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;
const QueueList = styled.div`
  display: grid;
  gap: var(--space-2);
`;
const QueueButton = styled.button<{ $selected?: boolean }>`
  width: 100%;
  padding: var(--space-3);
  border: 1px solid
    ${(p) =>
      p.$selected ? "var(--color-accent)" : "var(--color-border-subtle)"};
  border-radius: var(--radius-md);
  background: ${(p) =>
    p.$selected ? "var(--color-surface-subtle)" : "var(--color-surface)"};
  color: var(--color-text);
  text-align: left;
  cursor: pointer;
`;
const Preview = styled.pre`
  max-height: 420px;
  overflow: auto;
  margin: var(--space-3) 0 0;
  padding: var(--space-3);
  border-radius: var(--radius-md);
  background: var(--color-surface-inset);
  color: var(--color-text-secondary);
  font: var(--font-size-xs) / 1.5 var(--font-mono);
  white-space: pre-wrap;
`;
const Empty = styled.div`
  padding: var(--space-6);
  color: var(--color-text-secondary);
  text-align: center;
`;

const pct = (value: number | null | undefined) =>
  value === null || value === undefined ? "—" : `${(value * 100).toFixed(1)}%`;
const duration = (value: number | null | undefined) =>
  value === null || value === undefined
    ? "—"
    : `${Math.round(value / 1000)}s`;
const healthTone = (row: Record<string, any>) =>
  row.status === "insufficient_evidence"
    ? "neutral"
    : row.score >= 80
    ? "good"
    : row.score >= 55
    ? "warn"
    : "bad";

const LessonExperimentsTab = ({ communityId }: Props) => {
  const [reports, setReports] = useState<HealthReport[]>([]);
  const [agent, setAgent] = useState<Record<string, any>>({});
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [history, setHistory] = useState<QueueItem[]>([]);
  const [selectedPlanId, setSelectedPlanId] = useState("");
  const [selectedTaskId, setSelectedTaskId] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!communityId) return;
    const unsubscribers = [
      firestore
        .collection(DB_PATH.LEARNING_PLAN_EXPERIMENT_AGENT)
        .doc("portfolio")
        .onSnapshot(
          (snap) => setAgent(snap.data() || {}),
          (reason) => setError(reason.message)
        ),
      firestore
        .collection(DB_PATH.LEARNING_PLAN_HEALTH)
        .where("communityId", "==", communityId)
        .onSnapshot(
          (snap) => {
            const rows = snap.docs.map((doc) => ({
              id: doc.id,
              ...(doc.data() as Omit<HealthReport, "id">),
            }));
            setReports(rows);
            setSelectedPlanId((current) => current || rows[0]?.planId || "");
          },
          (reason) => setError(reason.message)
        ),
      firestore
        .collection(DB_PATH.LEARNING_PLAN_EXPERIMENT_AGENT)
        .doc("portfolio")
        .collection(DB_PATH.LEARNING_PLAN_EXPERIMENT_QUEUE)
        .where("communityId", "==", communityId)
        .onSnapshot(
          (snap) =>
            setQueue(
              snap.docs
                .map(
                  (doc) =>
                    ({ id: doc.id, ...doc.data() } as QueueItem)
                )
                .sort(
                  (left, right) =>
                    Number(right.priority || 0) - Number(left.priority || 0)
                )
            ),
          (reason) => setError(reason.message)
        ),
      firestore
        .collection(DB_PATH.LEARNING_PLAN_EXPERIMENT_HISTORY)
        .where("communityId", "==", communityId)
        .onSnapshot(
          (snap) =>
            setHistory(
              snap.docs.map(
                (doc) =>
                  ({ id: doc.id, ...doc.data() } as QueueItem)
              )
            ),
          (reason) => setError(reason.message)
        ),
    ];
    return () => unsubscribers.forEach((unsubscribe) => unsubscribe());
  }, [communityId]);

  const report = useMemo(
    () => reports.find((item) => item.planId === selectedPlanId),
    [reports, selectedPlanId]
  );
  const planQueue = queue.filter(
    (item) => !selectedPlanId || item.planId === selectedPlanId
  );
  const selectedTask =
    queue.find((item) => item.id === selectedTaskId) || planQueue[0];

  return (
    <Shell>
      <Card>
        <Header>
          <div>
            <Title>Learning plan health</Title>
            <Muted>
              Completion uses member progress. Reading, media, and continuation
              events explain behavior without replacing that ground truth.
            </Muted>
            <Muted>
              Weekly changed-plan scan · minimum reach{" "}
              {agent.policy?.health?.minReach || 50} · up to{" "}
              {agent.policy?.cadence?.maxNewExperimentsPerRun || 5} new and{" "}
              {agent.policy?.cadence?.maxConcurrentExperiments || 5} active
              experiments. Last cycle: {agent.lastWeeklyCycleKey || "not run"}.
            </Muted>
          </div>
          <Select
            value={selectedPlanId}
            onChange={(event) => {
              setSelectedPlanId(event.target.value);
              setSelectedTaskId("");
            }}
          >
            {reports.map((item) => (
              <option key={item.planId} value={item.planId}>
                {item.planTitle}
              </option>
            ))}
          </Select>
        </Header>
        {error ? <Empty>{error}</Empty> : null}
        {!report ? (
          <Empty>
            No materialized health report yet. Run the LENS health sync; it
            diagnoses data only and does not start an experiment.
          </Empty>
        ) : (
          <>
            <Metrics>
              <Metric>
                <MetricLabel>Enrollments</MetricLabel>
                <MetricValue>{report.funnel?.enrollments || 0}</MetricValue>
              </Metric>
              <Metric>
                <MetricLabel>Plan starts</MetricLabel>
                <MetricValue>{report.funnel?.planStarts || 0}</MetricValue>
              </Metric>
              <Metric>
                <MetricLabel>Plan completion</MetricLabel>
                <MetricValue>
                  {pct(report.funnel?.planCompletionRate)}
                </MetricValue>
              </Metric>
            </Metrics>
            <TableWrap>
              <Table>
                <thead>
                  <tr>
                    <th>Step</th>
                    <th>Health</th>
                    <th>Reached</th>
                    <th>Completed / reached</th>
                    <th>Median active</th>
                    <th>Reopen no complete</th>
                    <th>Continue 1h</th>
                    <th>Continue 24h</th>
                    <th>Continue 7d</th>
                    <th>Abandon</th>
                    <th>D7 return</th>
                  </tr>
                </thead>
                <tbody>
                  {(report.funnel?.steps || []).map((step) => (
                    <tr key={step.stepId}>
                      <td>
                        {step.position}. {step.title}
                        <br />
                        <Muted>v{step.contentVersion || 1}</Muted>
                      </td>
                      <td>
                        <Badge $tone={healthTone(step) as any}>
                          {step.status === "insufficient_evidence"
                            ? "Insufficient evidence"
                            : `${step.score}/100`}
                        </Badge>
                        {step.reasons?.length ? (
                          <Muted>{step.reasons.join(", ")}</Muted>
                        ) : null}
                      </td>
                      <td>{step.reached}</td>
                      <td>
                        {step.completed} / {step.reached} (
                        {pct(step.completionRate)})
                      </td>
                      <td>{duration(step.medianActiveTimeMs)}</td>
                      <td>{pct(step.reopenWithoutCompletionRate)}</td>
                      <td>{pct(step.nextStepContinuation1hRate)}</td>
                      <td>{pct(step.nextStepContinuation24hRate)}</td>
                      <td>{pct(step.nextStepContinuation7dRate)}</td>
                      <td>{pct(step.abandonmentAfterStepRate)}</td>
                      <td>{pct(step.d7ReturnRate)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableWrap>
          </>
        )}
      </Card>

      <Card>
        <Header>
          <div>
            <Title>Ranked improvement queue</Title>
            <Muted>
              One declared mutation at a time. Production starts remain
              separately authorized through LENS.
            </Muted>
          </div>
        </Header>
        <QueueGrid>
          <QueueList>
            {!planQueue.length ? <Empty>No queued lessons.</Empty> : null}
            {planQueue.map((item) => (
              <QueueButton
                key={item.id}
                $selected={selectedTask?.id === item.id}
                onClick={() => setSelectedTaskId(item.id)}
              >
                <strong>
                  {item.stepPosition}. {item.stepTitle}
                </strong>
                <Muted>
                  Priority {item.priority || 0} · {item.status}
                </Muted>
                {item.blockedReasons?.length ? (
                  <Muted>{item.blockedReasons.join(", ")}</Muted>
                ) : null}
              </QueueButton>
            ))}
          </QueueList>
          <div>
            {!selectedTask ? (
              <Empty>Select a lesson to inspect it.</Empty>
            ) : (
              <>
                <Title>{selectedTask.stepTitle}</Title>
                <Muted>
                  {selectedTask.hypothesis || "No candidate drafted yet."}
                </Muted>
                <Muted>
                  Mutation: {selectedTask.mutation || "—"} · Status:{" "}
                  {selectedTask.status}
                </Muted>
                {selectedTask.controlLesson ? (
                  <>
                    <Muted>Control preview (complete snapshot)</Muted>
                    <Preview>
                      {JSON.stringify(selectedTask.controlLesson, null, 2)}
                    </Preview>
                  </>
                ) : null}
                {selectedTask.candidateLesson ? (
                  <>
                    <Muted>Candidate preview (complete snapshot)</Muted>
                    <Preview>
                      {JSON.stringify(selectedTask.candidateLesson, null, 2)}
                    </Preview>
                  </>
                ) : null}
              </>
            )}
          </div>
        </QueueGrid>
      </Card>

      <Card>
        <Title>Experiment history</Title>
        <Muted>
          Control and candidate lineage is retained with every verdict.
        </Muted>
        <TableWrap>
          <Table>
            <thead>
              <tr>
                <th>Lesson</th>
                <th>Mutation</th>
                <th>Status</th>
                <th>Verdict</th>
                <th>Started</th>
              </tr>
            </thead>
            <tbody>
              {history
                .filter(
                  (item) => !selectedPlanId || item.planId === selectedPlanId
                )
                .map((item) => (
                  <tr key={item.id}>
                    <td>{item.stepTitle || item.stepId}</td>
                    <td>{item.mutation || "—"}</td>
                    <td>{item.status}</td>
                    <td>{item.verdict || item.evaluation?.verdict || "—"}</td>
                    <td>{item.startedAt || "—"}</td>
                  </tr>
                ))}
            </tbody>
          </Table>
        </TableWrap>
      </Card>
    </Shell>
  );
};

export default LessonExperimentsTab;
